import { CREW_DELETE_DEACTIVATE_MODES } from "../constants"

/**
 * Determines which deactivate/delete modal mode to use based on crew member data
 * @param {Object} crewDetails - Detailed crew member data from API
 * @param {string} crewDetails.mode - Direct mode from API (preferred)
 * @param {string} crewDetails.status - API status: "no_experiences_assigned" | "no_active_bookings" | "fully_substitutable" | "partially_substitutable"
 * @param {Array} crewDetails.assigned_trips - Array of assigned trips/experiences
 * @param {Array} crewDetails.active_bookings - Array of active bookings
 * @returns {string} One of CREW_DELETE_DEACTIVATE_MODES
 */
export const determineDeactivateMode = (crewDetails) => {
  const {
    mode,
    status,
    assigned_trips = [],
    active_bookings = []
  } = crewDetails || {}

  // Priority 1: If API provides mode field directly, use it
  if (mode) {
    return mode
  }

  // Priority 2: If API provides status field, map it to mode
  if (status) {
    const statusModeMap = {
      no_experiences_assigned: CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES,
      no_active_bookings: CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING,
      fully_substitutable: CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE,
      partially_substitutable: CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE
    }
    return statusModeMap[status] || CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  }

  // Fallback: Calculate mode from data if status not provided
  const assignedTripsCount = assigned_trips.length
  const activeBookingsCount = active_bookings.length

  // Scenario 1: No experiences assigned, no bookings
  if (assignedTripsCount === 0 && activeBookingsCount === 0) {
    return CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  }

  // Scenario 2: Has assigned experiences but no active bookings
  if (assignedTripsCount > 0 && activeBookingsCount === 0) {
    return CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING
  }

  // Scenario 3 & 4: Has upcoming bookings
  if (activeBookingsCount > 0) {
    // Check if all bookings can be automatically substituted
    const allBookingsSubstitutable = active_bookings.every(
      (booking) => booking.can_substitute === true
    )

    if (allBookingsSubstitutable) {
      return CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE
    } else {
      return CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE
    }
  }

  // Default to no experiences mode
  return CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
}
